import { Badge } from "@hub/components/ui/badge";
import { cn } from "@hub/lib/utils";
import { apiFetch } from "@hub/ui/api";
import { useEffect, useState } from "react";

interface Stats {
  total: number;
  stale: number;
  converging: number;
  unpinned: number;
  versions: { version: string | null; count: number }[];
}

/**
 * The fleet at a glance, above the table.
 *
 * Every figure here answers an operational question — is the fleet current, is
 * anything unreachable — and none of it says anything about what people do on
 * their instances. That boundary is enforced in the schema, not just here: the
 * heartbeat this is derived from has an exact column-set test.
 *
 * A count of zero is rendered muted rather than hidden, so "nothing is stale"
 * and "we have not loaded yet" never look the same.
 */
export function FleetStats() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<Stats>("/api/fleet/stats")
      .then(setStats)
      .catch(() => setError("Could not load fleet stats."));
  }, []);

  if (error) {
    return (
      <p className="mb-6 rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
        {error}
      </p>
    );
  }

  // Reserves the real height of the loaded row, so the table below does not
  // jump once the numbers arrive.
  if (!stats) return <div className="mb-6 h-[86px]" aria-hidden="true" />;

  const figures = [
    { label: "Instances", value: stats.total, hint: "enrolled and not decommissioned" },
    { label: "Stale", value: stats.stale, hint: "no heartbeat recently", alert: stats.stale > 0 },
    { label: "Converging", value: stats.converging, hint: "running ≠ desired" },
    { label: "Unpinned", value: stats.unpinned, hint: "no desired version set" },
  ];

  return (
    <section className="mb-6" aria-label="Helm Hub summary">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {figures.map((f) => (
          <div key={f.label} className="rounded-xl border border-border bg-card px-4 py-3">
            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {f.label}
            </dt>
            <dd
              className={cn(
                "font-display text-3xl font-semibold tabular-nums leading-tight",
                f.alert ? "text-[var(--warning)]" : "text-foreground",
                f.value === 0 && "text-muted-foreground",
              )}
            >
              {f.value}
            </dd>
            <p className="mt-0.5 text-xs text-muted-foreground">{f.hint}</p>
          </div>
        ))}

        <div className="col-span-2 rounded-xl border border-border bg-card px-4 py-3 sm:col-span-3 lg:col-span-1">
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Versions in use
          </dt>
          <dd className="mt-2 flex flex-wrap gap-1.5">
            {stats.versions.length > 0 ? (
              stats.versions.map((v) => (
                <Badge key={v.version ?? "unknown"} variant="outline" className="font-normal">
                  {v.version ?? "unknown"}
                  <span className="ml-1.5 tabular-nums text-muted-foreground">{v.count}</span>
                </Badge>
              ))
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </dd>
        </div>
      </dl>
    </section>
  );
}
